import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { api } from '../../services/api';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  useScrollTrigger,
  Slide,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Menu as MenuIcon,
  Business,
  Assignment,
  AccountCircle,
  Logout,
  History,
  Settings,
  Notifications,
  Schedule,
  Visibility,
  Stop as StopIcon,
  Flag as FlagIcon,
  WbSunny,
  NightsStay,
  AutoAwesome,
  Stars,
  Palette,
  ViewQuilt,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import type { NavMenuItem } from './navConfig';
import NavItem from './NavItem';
import { getChrome } from '../../theme/chrome';
import { alpha } from '@mui/material/styles';
import { useNavMenu } from './useNavMenu';
import { useNavigate, useLocation } from 'react-router-dom';
import { useImpersonatedAuth } from '../../hooks/useImpersonatedAuth';
import { useComponentAccess } from '../../hooks/useComponentAccess';
import { useTheme as useCustomTheme } from '../../contexts/ThemeContext';
import ImpersonationToggle from '../Impersonation/ImpersonationToggle';
import NotificationCenter from '../Notifications/NotificationCenter';
import GlobalSearch from '../Search/GlobalSearch';
import CommandPalette from '../Search/CommandPalette';
import KeyboardShortcutsDialog from '../Search/KeyboardShortcutsDialog';
import RecentlyViewedList from './RecentlyViewedList';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { useAppMode } from '../../contexts/AppModeContext';
import ModeSwitcher from '../ModeSwitcher/ModeSwitcher';
import VersionDisplay from '../Version/VersionDisplay';
import ProfileAvatar from '../Profile/ProfileAvatar';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import DailyHoroscope from '../Horoscope/DailyHoroscope';
import { useWeather } from '../../contexts/WeatherContext';
import WeatherOverlay from '../Weather/WeatherOverlay';
import '../Weather/WeatherAnimations.css';
import { canSeeAssistantEntry } from '../../utils/assistantAccess';
import { getEnvironmentBadge, type EnvironmentBadge } from '../../config/environment';
import EnvironmentWarningDialog from '../Environment/EnvironmentWarningDialog';


const drawerWidth = 240;

// Identify which environment the user is currently on. Returns null in
// production so the AppBar gets no extra colouring. The hook is consumed in
// two places: an inline pill that only renders on >=sm, and the mobile burger
// button which uses the colour as its background tint so the env hint stays
// visible without consuming any horizontal space.
// Detection + presentation live in config/environment.ts (single source, also
// used by the favicon badge, VersionDisplay, and the non-prod login warning).
const useEnvironment = (): EnvironmentBadge | null => getEnvironmentBadge();

const EnvironmentIndicator = () => {
  const env = useEnvironment();
  if (!env) return null;
  return (
    <Tooltip title={env.tooltip}>
      <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 0.5, mr: 1 }}>
        <FlagIcon sx={{ color: env.bg }} />
        <Typography
          variant="caption"
          sx={{
            color: env.fg,
            backgroundColor: env.bg,
            px: 1,
            py: 0.25,
            borderRadius: '12px',
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.05em',
            lineHeight: 1.4,
            textTransform: 'uppercase',
          }}
        >
          {env.label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

/**
 * Overlays a colored environment badge on the browser favicon using canvas.
 * Red "DEV" for localhost, yellow "STG" for staging, no overlay for production.
 */
function applyFaviconBadge() {
  const badge = getEnvironmentBadge();
  if (!badge) return; // Production — no badge
  const label = badge.shortLabel;
  const bgColor = badge.bg;
  const textColor = badge.fg;

  const favicon = document.querySelector<HTMLLinkElement>('link[rel*="icon"]');
  if (!favicon) return;

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const size = 64;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Draw original favicon
    ctx.drawImage(img, 0, 0, size, size);

    // Draw badge background (bottom-right corner)
    const badgeH = 22;
    const badgeY = size - badgeH;
    ctx.fillStyle = bgColor;
    ctx.beginPath();
    ctx.roundRect(0, badgeY, size, badgeH, [0, 0, 4, 4]);
    ctx.fill();

    // Draw badge text
    ctx.fillStyle = textColor;
    ctx.font = 'bold 16px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, size / 2, badgeY + badgeH / 2);

    // Replace favicon
    favicon.href = canvas.toDataURL('image/png');
  };
  img.src = favicon.href;
}

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const env = useEnvironment();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [horoscopeAnchor, setHoroscopeAnchor] = useState<null | HTMLElement>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  // Overlay environment badge on browser favicon (dev=red, stage=yellow, prod=none)
  useEffect(() => { applyFaviconBadge(); }, []);

  const { user, logout } = useImpersonatedAuth();
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation();
  const { hasAccess, componentAccess, isLoading: isAccessLoading } = useComponentAccess();
  const { data: userCrmAccessData } = useQuery({
    queryKey: ['userCrmAccess', user?.id],
    queryFn: async () => {
      const response = await api.get('/users/me/crm-access');
      return response.data?.access_level;
    },
    enabled: !!user?.id,
  });

  const { data: inventoryStats } = useQuery({
    queryKey: ['inventory-dashboard-stats'],
    queryFn: async () => {
      const response = await api.get('/inventory/dashboard/stats');
      return response.data?.data;
    },
    enabled: hasAccess('inventory-order-requests'),
    refetchInterval: 30000 // Refetch every 30 seconds to keep badges somewhat real-time
  });

  // Restricted (salesperson) CRM users reach Order Requests via the CRM role,
  // not an inventory component grant, so the dashboard-stats query above is
  // disabled for them (they have no inventory-dashboard access). Fall back to a
  // scoped count endpoint that returns only their client-scoped pending count.
  const isCrmRestricted = userCrmAccessData === 'restricted';
  const { data: restrictedPendingCount } = useQuery({
    queryKey: ['order-requests-pending-count', user?.id],
    queryFn: async () => {
      const response = await api.get('/inventory/order-requests/pending-count');
      return response.data?.data?.count ?? 0;
    },
    enabled: isCrmRestricted,
    refetchInterval: 30000
  });

  const pendingOrderRequests = isCrmRestricted
    ? (restrictedPendingCount || 0)
    : (inventoryStats?.orderRequests?.pending || 0);

  // Department leads: count of unassigned work routed to the departments they
  // lead (cross-company), for the red badge on the "My Assignments" nav item.
  // Returns 0 for everyone who leads no department, so the badge stays hidden.
  const { data: deptPendingCount } = useQuery({
    queryKey: ['dept-pending-count', user?.id],
    queryFn: async () => {
      const response = await api.get('/departments/led/pending-count');
      return response.data?.data?.count ?? 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000
  });

  const impersonatedDisplayName =
    (
      `${impersonatedUser?.firstName || impersonatedUser?.first_name || ''} ${impersonatedUser?.lastName || impersonatedUser?.last_name || ''}`
    ).trim() || impersonatedUser?.email || 'user';
  const { themeMode, setThemeMode, toggleTheme } = useCustomTheme();
  const chrome = getChrome(themeMode);
  const [shortcutsOpen, setShortcutsOpen] = React.useState(false);
  useKeyboardShortcuts({
    onShowHelp: () => setShortcutsOpen(true)
  });

  // First-time discoverability hint: surface the shortcuts dialog once per
  // browser via a low-friction toast. localStorage flag keyed by user id so
  // shared machines don't double-prompt the same person.
  React.useEffect(() => {
    if (!user?.id) return;
    const key = `crm_shortcut_hint_seen_${user.id}`;
    if (localStorage.getItem(key)) return;
    const t = setTimeout(() => {
      toast.info('Tip: press ? to see keyboard shortcuts, or ⌘K to open the command palette.', { autoClose: 8000 });
      try { localStorage.setItem(key, '1'); } catch { /* private mode */ }
    }, 1500);
    return () => clearTimeout(t);
  }, [user?.id]);
  const { weather } = useWeather();
  const { mode } = useAppMode();
  const navigate = useNavigate();
  const location = useLocation();
  const isWeatherEnabled = user?.preferences?.weather_overlay_enabled !== false && weather.condition !== 'Unknown';
  // Dark chrome goes see-through over the weather overlay, except on themes
  // (slate) that keep a solid shell.
  const chromeGlass = isWeatherEnabled && chrome.weatherGlass;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Facebook-style hide-on-scroll for the top AppBar on mobile only.
  // The <Box component="main"> below uses overflow:auto, so the window
  // doesn't scroll — we have to point useScrollTrigger at that element.
  const [mainScrollEl, setMainScrollEl] = useState<HTMLElement | null>(null);
  const hideAppBarOnScroll = useScrollTrigger({
    target: mainScrollEl ?? undefined,
    threshold: 64,
    disableHysteresis: false,
  });
  const showAppBar = !isMobile || !hideAppBarOnScroll;

  const stopImpersonationControl = isImpersonating ? (

    <Tooltip
      title={`Stop impersonating ${impersonatedDisplayName}`}
    >
      <IconButton
        onClick={stopImpersonation}
        color="warning"
        size="small"
        sx={{
          backgroundColor: 'rgba(255, 152, 0, 0.12)',
          '&:hover': {
            backgroundColor: 'rgba(255, 152, 0, 0.24)',
          },
        }}
      >
        <StopIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  // Reset collapsed groups when portal mode changes
  useEffect(() => {
    setOpenGroups({});
  }, [mode]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setMobileOpen(!mobileOpen);
    } else {
      setDesktopOpen(!desktopOpen);
    }
  };

  const handleGroupToggle = (group: string) => {
    setOpenGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleProfileMenuClose();
  };

  // Sidebar menu for the current portal mode, filtered by the user's access — see ./useNavMenu.
  const filteredMenuStructure = useNavMenu({
    deptPendingCount,
    pendingOrderRequests,
    user,
    userCrmAccessData,
  });

  const drawer = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ minHeight: '64px' }} />
      <Divider sx={chrome.toolbarDivider ? { borderColor: chrome.toolbarDivider } : undefined} />
      <List sx={{
        backgroundColor: chrome.dark
          ? (chromeGlass ? 'transparent' : chrome.bg)
          : 'inherit',
        color: chrome.dark ? '#ffffff' : 'inherit',
        flex: 1,
        px: 1
      }}>
        {/* Compact icon row that hosts quick-jump affordances (Calendar
            at the start, Recently Viewed at the end). Sits inside the
            main List so it shares the same vertical rhythm. Future
            quick-jump icons get added between Calendar and the flex
            spacer. */}
        <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, mb: 0.5, gap: 0.5 }}>
          <Tooltip title="Calendar">
            <IconButton
              size="small"
              onClick={() => navigate('/calendar')}
              sx={{ color: 'inherit', opacity: 0.85 }}
            >
              <CalendarMonthIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          <RecentlyViewedList />
        </Box>
        {filteredMenuStructure.map((item: NavMenuItem) => (
          <NavItem
            key={item.path || item.group}
            item={item}
            pathname={location.pathname}
            themeMode={themeMode}
            openGroups={openGroups}
            onNavigate={(path) => { navigate(path); if (isMobile) setMobileOpen(false); }}
            onGroupToggle={handleGroupToggle}
          />
        ))}
      </List>

      {isMobile && (
        <Box sx={{
          backgroundColor: chrome.dark 
            ? (chromeGlass ? 'transparent' : chrome.bg) 
            : 'inherit',
          color: chrome.dark ? '#ffffff' : 'inherit',
          borderTop: chrome.dark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.12)'
        }}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleProfileMenuOpen} sx={{ px: 2, py: 1.25, '&:hover': { backgroundColor: alpha(chrome.accent, 0.1) } }}>
              <ListItemIcon sx={{ minWidth: 40 }}>
                <ProfileAvatar
                  profilePicture={user?.profilePicture}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  email={user?.email}
                  userId={user?.id}
                  size="small"
                />
              </ListItemIcon>
              <ListItemText
                primary={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Profile'}
                secondary={user?.email}
                sx={{
                  '& .MuiTypography-root': { fontSize: '0.875rem' },
                  '& .MuiTypography-root + .MuiTypography-root': { fontSize: '0.75rem', opacity: 0.8 }
                }}
              />
            </ListItemButton>
          </ListItem>
        </Box>
      )}

      {/* Version Display at bottom of navigation */}
      <Box sx={{
        mt: 'auto',
        px: 1.5,
        pb: 1,
        pt: 0.5,
        backgroundColor: chrome.dark 
          ? (chromeGlass ? 'transparent' : chrome.bg) 
          : 'inherit',
        color: chrome.dark ? '#ffffff' : 'inherit',
        borderTop: '1px solid',
        borderColor: chrome.divider,
      }}>
        <VersionDisplay
          variant="navbar"
          showIcon={false}
          showChangelog={true}
        />
      </Box>
    </div>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', minHeight: '100vh', position: 'relative' }}>

      <Slide appear={false} direction="down" in={showAppBar}>
      <AppBar

        position="fixed"
        sx={{
          width: '100%',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: chrome.dark
            ? (chromeGlass ? 'transparent !important' : chrome.bg)
            : 'inherit',
          color: chrome.dark ? '#ffffff' : 'inherit',
          boxShadow: chrome.dark ? 'none' : 'inherit',
        }}
      >
        <Toolbar 
          className={chromeGlass ? 'glass-overlay' : ''}
          sx={{
            backgroundColor: chrome.dark 
              ? (chromeGlass ? 'transparent' : chrome.bg) 
              : 'inherit',
            color: chrome.dark ? '#ffffff' : 'inherit',
            position: 'relative',
            minHeight: '64px'
          }}
        >
          {/* Menu button — on mobile, env colour (Dev/Stage) tints the
              background so the environment hint stays visible without using
              its own slot. Desktop keeps the inline pill below. */}
          <Tooltip title={env?.tooltip || ''} disableHoverListener={!env}>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                mr: 2,
                ...(env && {
                  backgroundColor: { xs: env.bg, sm: 'transparent' },
                  color: { xs: env.fg, sm: 'inherit' },
                  borderRadius: { xs: '8px', sm: '50%' },
                }),
                '&:hover': {
                  backgroundColor: env
                    ? { xs: env.bg, sm: chrome.hover }
                    : chrome.hover
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>

          <EnvironmentIndicator />

          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {/* User's Motto - Right after title */}
            {user?.quote && (
              <Typography
                variant="body2"
                sx={{
                  ml: 2,
                  pl: 2,
                  fontStyle: 'italic',
                  color: chrome.dark ? '#ffffff' : 'inherit',
                  opacity: 0.85,
                  maxWidth: { lg: 500 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: { xs: 'none', lg: 'block' }
                }}
              >
                "{user.quote}"
              </Typography>
            )}
          </Box>

          {/* Right side - Action buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'flex-end', gap: 2 }}>
            <ModeSwitcher />

            {isMobile && stopImpersonationControl}


            <GlobalSearch themeMode={themeMode} />

            <NotificationCenter
              themeMode={themeMode}
            />

            {/* AI Assistant — system admins only for now (docs/features/sales-assistant.md → "Where to find it").
                On mobile (below md) it is in the account menu instead. */}
            {!isMobile && canSeeAssistantEntry(user) && (
              <Tooltip title="AI Assistant">
                <IconButton
                  size="large"
                  color="inherit"
                  aria-label="AI Assistant"
                  onClick={() => navigate('/assistant')}
                  sx={{
                    '&:hover': {
                      backgroundColor: chrome.hover
                    }
                  }}
                >
                  <AutoAwesome />
                </IconButton>
              </Tooltip>
            )}


            {!isMobile && stopImpersonationControl}

            {!isMobile && (
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
                sx={{
                  '&:hover': {
                    backgroundColor: chrome.hover
                  }
                }}
              >
                <ProfileAvatar
                  profilePicture={user?.profilePicture}
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  email={user?.email}
                  userId={user?.id}
                  size="small"
                />
              </IconButton>
            )}
          </Box>
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                minWidth: 320, // Increased for weather widget
                mt: 1.5,
                border: '1px solid #ff8c00',
                boxShadow: '0 0 0 2px rgba(255, 140, 0, 0.1)',
                '@keyframes menuGradientSweep': {
                  '0%': { transform: 'translateX(-120%)', opacity: 0 },
                  '20%': { opacity: 1 },
                  '100%': { transform: 'translateX(120%)', opacity: 0 }
                },
                '& .MuiMenuItem-root': {
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: 1,
                  mx: 0.5,
                  transition: 'background-color 200ms ease, color 200ms ease',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: '55%',
                    background: 'linear-gradient(90deg, rgba(255,140,0,0) 0%, rgba(255,140,0,0.18) 40%, rgba(255,140,0,0.10) 70%, rgba(255,140,0,0) 100%)',
                    pointerEvents: 'none',
                    transform: 'translateX(-120%)',
                    opacity: 0
                  },
                  '&:hover::after': {
                    animation: 'menuGradientSweep 700ms ease-out'
                  },
                  '&:hover .MuiListItemIcon-root, &:hover .MuiListItemText-root': {
                    color: '#ff8c00'
                  }
                },
                '& .MuiMenuItem-root.Mui-selected': {
                  backgroundColor: '#ff8c00',
                  color: '#000000'
                },
                '& .MuiMenuItem-root.Mui-selected .MuiListItemIcon-root, & .MuiMenuItem-root.Mui-selected .MuiListItemText-root': {
                  color: '#000000'
                }
              }
            }}
          >
            {/* Weather Widget inside Profile Menu */}
            <Box sx={{ px: 2, py: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', mb: 1 }}>
              <Typography variant="overline" sx={{ display: 'block', mb: 1.5, color: '#ff8c00', fontWeight: 800, letterSpacing: 1.2 }}>
                Atmosphere
              </Typography>
              <WeatherOverlay />
            </Box>
            <Box sx={{
              px: 3,
              py: 2.5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1
            }}>
              <ProfileAvatar
                profilePicture={user?.profilePicture}
                firstName={user?.firstName}
                lastName={user?.lastName}
                email={user?.email}
                userId={user?.id}
                size={56}
              />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center' }}>
                {user?.firstName} {user?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                {user?.email}
              </Typography>
            </Box>
            <Divider />

            {isMobile && canSeeAssistantEntry(user) && (
              <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/assistant'); }}>
                <ListItemIcon>
                  <AutoAwesome fontSize="small" />
                </ListItemIcon>
                AI Assistant
              </MenuItem>
            )}

            <MenuItem
              onClick={(e) => {
                setHoroscopeAnchor(anchorEl);
                handleProfileMenuClose();
                e.stopPropagation();
              }}
            >
              <ListItemIcon>
                <Stars fontSize="small" />
              </ListItemIcon>
              Daily Horoscope
            </MenuItem>

            <MenuItem
              disableRipple
              sx={{ cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
            >
              <ListItemIcon>
                <Palette fontSize="small" />
              </ListItemIcon>
              <ListItemText>Theme</ListItemText>
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                {([
                  { mode: 'light' as const, icon: <WbSunny fontSize="small" />, label: 'Light' },
                  { mode: 'dark' as const, icon: <NightsStay fontSize="small" />, label: 'Dark' },
                  { mode: 'orange' as const, icon: <Business fontSize="small" />, label: 'Orange' },
                  { mode: 'slate' as const, icon: <ViewQuilt fontSize="small" />, label: 'Slate' },
                ]).map((t) => {
                  const selected = themeMode === t.mode;
                  return (
                    <Tooltip key={t.mode} title={t.label}>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); setThemeMode(t.mode); }}
                        sx={{
                          color: selected ? chrome.accent : 'inherit',
                          bgcolor: selected ? alpha(chrome.accent, 0.12) : 'transparent',
                          border: selected ? `1px solid ${chrome.accent}` : '1px solid transparent',
                          '&:hover': { bgcolor: alpha(chrome.accent, 0.2) }
                        }}
                      >
                        {t.icon}
                      </IconButton>
                    </Tooltip>
                  );
                })}
              </Box>
            </MenuItem>

            <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            {user?.is_system_admin && (
              <MenuItem onClick={() => { navigate('/config'); handleProfileMenuClose(); }}>
                <ListItemIcon>
                  <Settings fontSize="small" />
                </ListItemIcon>
                Configuration
              </MenuItem>
            )}
            {user?.is_system_admin && (
              <MenuItem onClick={() => { navigate('/admin/cron-jobs'); handleProfileMenuClose(); }}>
                <ListItemIcon>
                  <Schedule fontSize="small" />
                </ListItemIcon>
                Cron Jobs
              </MenuItem>
            )}
            {user?.is_system_admin && (
              <MenuItem onClick={() => { navigate('/admin/escalation-rules'); handleProfileMenuClose(); }}>
                <ListItemIcon>
                  <Notifications fontSize="small" />
                </ListItemIcon>
                Escalation Rules
              </MenuItem>
            )}
            {user?.is_system_admin && (
              <MenuItem>
                <ListItemIcon>
                  <Visibility fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  Impersonation
                </ListItemText>
                <Box sx={{ ml: 1 }}>
                  <ImpersonationToggle />
                </Box>
              </MenuItem>
            )}
            <Divider />
            <MenuItem onClick={() => { navigate('/portal-request'); handleProfileMenuClose(); }}>
              <ListItemIcon>
                <Assignment fontSize="small" />
              </ListItemIcon>
              Submit Portal Request
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>

          {/* The horoscope opens from the account menu and reuses the
              profile-button anchor so the popover lands where the menu
              just was. */}
          <Menu
            anchorEl={horoscopeAnchor}
            id="horoscope-menu"
            open={Boolean(horoscopeAnchor)}
            onClose={() => setHoroscopeAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                width: 350,
                maxWidth: '100vw',
                mt: 1.5,
                padding: 0,
                overflow: 'hidden',
                borderRadius: 2,
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                border: '1px solid rgba(255, 140, 0, 0.2)',
              },
            }}
          >
            <Box sx={{ p: 0 }}>
              <DailyHoroscope birthMonthDay={user?.birthMonthDay} isFlat={true} />
            </Box>
          </Menu>
        </Toolbar>
      </AppBar >
      </Slide>
      <Box
        component="nav"
        sx={{ width: { md: (desktopOpen ? drawerWidth : 0) }, flexShrink: { md: 0 }, transition: 'width 0.3s ease' }}
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          open={isMobile ? mobileOpen : desktopOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              // Each theme's chrome / nav surface (see theme/chrome.ts). Orange
              // and dark use the historical navy at 0.7 alpha so the drawer
              // reads as 30% transparent over the weather overlay; light keeps
              // a white surface at the same opacity; slate is solid navy.
              backgroundColor: chrome.drawerBg,
              color: chrome.dark ? '#ffffff' : theme.palette.text.primary,
              borderRight: chrome.dark ? 'none' : 'inherit',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        ref={setMainScrollEl}
        sx={{
          position: 'absolute',
          left: { md: desktopOpen ? `${drawerWidth}px` : 0 },
          right: 0,
          // Drop main.top to 0 in lockstep with the mobile hide-on-scroll
          // AppBar so we don't leave a 56/64px transparent band at the top.
          // On md+ showAppBar is always true, so this resolves to the
          // original 64px and desktop behavior is unchanged.
          top: { xs: showAppBar ? 56 : 0, sm: showAppBar ? 64 : 0, md: 64 },
          bottom: 0,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { xs: '100%', md: desktopOpen ? `calc(100% - ${drawerWidth}px)` : '100%' },
          transition: 'left 0.3s ease, width 0.3s ease, top 195ms ease',
          minHeight: 'calc(100vh - 64px)',
          backgroundColor: chrome.mainBg,
          maxWidth: 'none',
          overflow: 'auto'
        }}
      >
        <Breadcrumbs />
        {user && !user.is_system_admin && !isAccessLoading && (!componentAccess || Object.keys(componentAccess).length === 0) && (
          <Alert severity="warning" variant="filled" sx={{ mb: 2, mt: 1 }}>
            You do not currently have any component permissions assigned. Please contact your company's administrator to have them added.
          </Alert>
        )}
        {children}
      </Box>
      {/* Non-production login warning — renders nothing in production, and at
          most once per browser session elsewhere. */}
      <EnvironmentWarningDialog />
      <CommandPalette
        themeMode={themeMode}
        onToggleTheme={toggleTheme}
        onLogout={handleLogout}
        onShowShortcuts={() => setShortcutsOpen(true)}
      />
      <KeyboardShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </Box >
  );
};

export default Layout;
